<script lang="ts">
	import { ReleaseClient } from '../api/client';
	import type { AssetRequest } from '../types/release';
	import { useReleaseContext } from '../context/release';
	import { useAssetGeneration } from '../services/useAssetGeneration';
	import { Download, Play, Volume2, Music, AlertTriangle, Rocket, RefreshCw } from 'lucide-svelte';

	const client = new ReleaseClient();

	let {
		ondeliver,
		onassetsGenerated,
		onerror
	}: {
		ondeliver?: () => void;
		onassetsGenerated?: (assets: { imageUrl: string | null; audioUrl: string | null; videoUrl: string | null }) => void;
		onerror?: (error: string) => void;
	} = $props();

	let lastTrigger = $state(0);

	const { releaseConfigState, releaseRuntimeState, releaseStore } = useReleaseContext();

	const assetGeneration = useAssetGeneration({
		client,
		callbacks: {
			onProgress: (message) => releaseStore.updateAssetProgress(message),
			onComplete: (results) => {
				releaseStore.setAssetResults(results);
				onassetsGenerated?.(results);
			},
			onError: (error) => {
				releaseStore.setAssetError(error.message);
				onerror?.(error.message);
			}
		}
	});

	let assetState = $derived($releaseRuntimeState.assetGeneration);
	let assetTriggerVersion = $derived($releaseRuntimeState.assetTriggerVersion);
	let useStockAssets = $derived(!$releaseConfigState.generateAiAssets);
	let isApple = $derived(($releaseConfigState.outputFormat ?? 'ddex-ern') === 'apple-itunes');

	// Stock asset URLs served through the same /api/assets/files/ path
	const stockAssetUrls = {
		imageUrl: '/api/assets/files/stock/image.jpg',
		audioUrl: '/api/assets/files/stock/audio.flac',
		videoUrl: '/api/assets/files/stock/video.mp4',
	};

	// Effective asset URLs: stock assets when AI is disabled, generated assets otherwise
	let effectiveAssets = $derived(
		useStockAssets
			? stockAssetUrls
			: assetState.results
	);

	// Show audio section: Audio/DjMix always, Video profiles only when AI-generated, Apple always (audio-only)
	let showAudioSection = $derived(
		isApple ||
		['Audio', 'DjMix'].includes($releaseConfigState.releaseProfile) ||
		(!useStockAssets && ['Video', 'LongFormMusicalWorkVideo'].includes($releaseConfigState.releaseProfile))
	);

	let showVideoSection = $derived(
		!isApple && ['Video', 'LongFormMusicalWorkVideo'].includes($releaseConfigState.releaseProfile)
	);

	// Trigger AI generation when enabled
	$effect(() => {
		if ($releaseConfigState.generateAiAssets && assetTriggerVersion > lastTrigger) {
			lastTrigger = assetTriggerVersion;
			queueMicrotask(startGeneration);
		}
	});

	function createSpinner(size: 'sm' | 'md' | 'lg' = 'md', color: string = 'blue-600') {
		const sizeClasses = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
		return `${sizeClasses[size]} border-2 border-${color} border-t-transparent rounded-full animate-spin`;
	}

	function createPulse() {
		return 'animate-pulse';
	}

	async function startGeneration() {
		const assetRequest: AssetRequest = {
			releaseId: $releaseConfigState.releaseId ?? { ICPN: 'default-icpn' },
			releaseProfile: $releaseConfigState.releaseProfile,
			releaseMetadata: $releaseRuntimeState.releaseMetadata ?? {
				title: 'Generated Release',
				artist: 'AI Generated Artist',
				genre: 'Electronic'
			}
		};

		await assetGeneration.startGeneration(assetRequest);
	}

	function handleDeliver() {
		ondeliver?.();
	}

	function handleRetryGeneration() {
		releaseStore.setPreviewRequested('assets');
	}
</script>

<div class="space-y-8">
	<!-- Release Preview with Assets -->
	{#if $releaseRuntimeState.releaseMetadata}
		<div class="card card-hover animate-slide-up">
			<div class="card-body">
				<div class="flex items-start gap-4 mb-6">
					<div class="avatar avatar-md bg-blue-600 text-white">
						<Music size={24} />
					</div>
					<div>
						<h2 class="text-2xl font-bold text-gray-900 mb-1">Release Preview</h2>
						<p class="text-gray-600">
							{#if useStockAssets}
								Stock assets will be used for this delivery
							{:else if assetState.isGenerating}
								Generating assets for this release
							{:else if assetState.results}
								Assets generated for this release
							{:else}
								Ready to generate assets for this release
							{/if}
						</p>
					</div>
				</div>

				<!-- Release Metadata -->
				<div class="grid grid-cols-1 md:grid-cols-2 gap-6 text-base mb-8">
					<div class="flex flex-col">
						<span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Title</span>
						<span class="text-lg text-gray-900 font-medium">{$releaseRuntimeState.releaseMetadata.title}</span>
					</div>
					<div class="flex flex-col">
						<span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Artist</span>
						<span class="text-lg text-gray-900 font-medium">{$releaseRuntimeState.releaseMetadata.artist}</span>
					</div>
					<div class="flex flex-col">
						<span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Genre</span>
						<span class="text-lg text-gray-900 font-medium">{$releaseRuntimeState.releaseMetadata.genre}</span>
					</div>
					<div class="flex flex-col">
						<span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Release Profile</span>
						<span class="text-lg text-gray-900 font-medium">{$releaseConfigState.releaseProfile}</span>
					</div>
					{#if $releaseConfigState.releaseId?.ICPN || $releaseConfigState.releaseId?.proprietaryId?.value}
						<div class="flex flex-col md:col-span-2">
							<span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
								{$releaseConfigState.releaseType === 'SingleResourceRelease' ? 'ISRC' : 'ICPN'}
							</span>
							<span class="text-lg text-gray-900 font-medium font-mono">
								{$releaseConfigState.releaseType === 'SingleResourceRelease'
									? $releaseConfigState.releaseId?.proprietaryId?.value
									: $releaseConfigState.releaseId?.ICPN}
							</span>
						</div>
					{/if}
				</div>

				<!-- Assets Section -->
				{#if effectiveAssets || assetState.isGenerating}
					<div class="border-t border-gray-200 pt-6">
						<h3 class="text-lg font-semibold text-gray-900 mb-4">
							{#if useStockAssets}
								Stock Assets Preview
							{:else if assetState.isGenerating}
								Generating Assets...
							{:else}
								Generated Assets
							{/if}
						</h3>

						<div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
							<!-- Cover Art -->
							<div class="card card-hover bg-gray-50">
								<div class="overflow-hidden rounded-t-xl">
									{#if effectiveAssets?.imageUrl}
										<img
											src={effectiveAssets.imageUrl}
											alt="Cover art for {$releaseRuntimeState.releaseMetadata?.title || 'release'}"
											class="w-full h-48 object-cover transition-opacity duration-300"
											loading="lazy"
										/>
									{:else}
										<div class="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center {createPulse()}">
											<div class="text-center">
												<div class={createSpinner('lg', 'gray-400')}></div>
												<p class="text-gray-500 text-sm mt-3 font-medium">Generating cover art...</p>
											</div>
										</div>
									{/if}
								</div>
								<div class="card-body">
									<h4 class="text-sm font-semibold text-gray-900">Cover Art</h4>
									<div class="flex gap-2 mt-3">
										{#if effectiveAssets?.imageUrl}
											<a
												href={effectiveAssets.imageUrl}
												download="cover-art.jpg"
												class="btn btn-sm btn-primary"
												aria-label="Download cover art image"
											>
												<Download size={14} />
												Download
											</a>
										{:else}
											<button class="btn btn-sm btn-ghost" disabled>
												<Download size={14} />
												Generating...
											</button>
										{/if}
									</div>
								</div>
							</div>

							<!-- Audio & Video -->
							<div class="lg:col-span-2 space-y-4">
							<!-- Audio Section -->
							{#if showAudioSection}
									<div class="card card-hover bg-gray-50">
										<div class="card-body">
											<h4 class="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
												<Volume2 size={16} />
												Audio Preview
											</h4>
											{#if effectiveAssets?.audioUrl}
												<audio
													controls
													src={effectiveAssets.audioUrl}
													class="w-full rounded-lg transition-opacity duration-300"
													aria-label="Audio preview for {$releaseRuntimeState.releaseMetadata?.title || 'release'}"
												>
													Your browser does not support the audio element.
												</audio>
											{:else}
												<div class="w-full h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg flex items-center justify-center {createPulse()}">
													<div class="flex items-center gap-3">
														<div class={createSpinner('sm', 'gray-500')}></div>
														<span class="text-gray-600 text-sm font-medium">Generating audio track...</span>
													</div>
												</div>
											{/if}
											<div class="flex gap-2 mt-3">
												{#if effectiveAssets?.audioUrl}
													<a
														href={effectiveAssets.audioUrl}
														download="audio-preview.mp3"
														class="btn btn-sm btn-secondary"
														aria-label="Download audio file"
													>
														<Download size={14} />
														Download Audio
													</a>
												{:else}
													<button class="btn btn-sm btn-ghost" disabled>
														<Download size={14} />
														Generating...
													</button>
												{/if}
											</div>
										</div>
									</div>
								{/if}

							<!-- Video Section -->
							{#if showVideoSection}
									<div class="card card-hover bg-gray-50">
										<div class="card-body">
											<h4 class="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
												<Play size={16} />
												Video Preview
											</h4>
											{#if effectiveAssets?.videoUrl}
												<video
													controls
													src={effectiveAssets.videoUrl}
													class="w-full rounded-lg transition-opacity duration-300"
													aria-label="Video preview for {$releaseRuntimeState.releaseMetadata?.title || 'release'}"
												>
													<track kind="captions" src="" label="No captions available" />
													Your browser does not support the video element.
												</video>
											{:else}
												<div class="w-full aspect-video bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center {createPulse()}">
													<div class="text-center">
														<div class={createSpinner('lg', 'gray-400')}></div>
														<p class="text-gray-500 text-sm mt-3 font-medium">Generating video content...</p>
													</div>
												</div>
											{/if}
											<div class="flex gap-2 mt-3">
												{#if effectiveAssets?.videoUrl}
													<a
														href={effectiveAssets.videoUrl}
														download="video-preview.mp4"
														class="btn btn-sm btn-accent"
														aria-label="Download video file"
													>
														<Download size={14} />
														Download Video
													</a>
												{:else}
													<button class="btn btn-sm btn-ghost" disabled>
														<Download size={14} />
														Generating...
													</button>
												{/if}
											</div>
										</div>
									</div>
								{/if}
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>
	{/if}

	<!-- Asset Generation Error -->
	{#if assetState.error && !$releaseConfigState.generateAiAssets}
		<div class="card">
			<div class="card-body">
				<div class="alert alert-error">
					<AlertTriangle size={20} />
					<div>
						<h3 class="font-bold">Generation Error</h3>
						<div class="text-xs">{$releaseRuntimeState.assetGeneration.error}</div>
					</div>
				</div>
			</div>
		</div>
	{/if}

	<!-- Action Buttons -->
	<div class="flex justify-center gap-4">
		{#if $releaseConfigState.generateAiAssets && assetState.error}
			<button class="btn btn-error" onclick={handleRetryGeneration}>
				<RefreshCw size={16} />
				Retry Generation
			</button>
		{:else}
			<button
				class="btn btn-primary"
				onclick={handleDeliver}
				disabled={
					$releaseConfigState.generateAiAssets
						? assetState.isGenerating || !assetState.results
						: false
				}
			>
				{#if $releaseConfigState.generateAiAssets && assetState.isGenerating}
					<div class={createSpinner('sm', 'white')}></div>
					Generating Assets...
				{:else}
					<Rocket size={16} />
					Continue to Delivery
				{/if}
			</button>
		{/if}
	</div>
</div>
